import {
  CalendarSubscriptionStateSchema, type CalendarSubscriptionMutation,
  NativeCalendarAccessGroupsSchema, NativeCalendarBootstrapSchema,
  NativeCalendarSnapshotSchema,
  NativeCalendarBookingResultSchema,
  NativeCalendarMutationResultSchema,
  NativeCalendarLeadResultSchema,
  NativeCalendarProfilesSchema,
  NativeCalendarBookingChannelsSchema,
  NativeCalendarSeriesListSchema,
  NativeCalendarEventSchema,
} from "@vekha/contracts";
import { ApiRequestError } from "./api";

export type CalendarBootstrap = ReturnType<typeof NativeCalendarBootstrapSchema.parse>;
export type CalendarSnapshot = ReturnType<typeof NativeCalendarSnapshotSchema.parse>;
export type CalendarBooking = CalendarSnapshot["bookings"][number];
export type CalendarEvent = CalendarSnapshot["events"][number];
export type CalendarSlot = CalendarSnapshot["slots"][number];
export type CalendarManager = CalendarBootstrap["managers"][number];
export type CalendarProfile = CalendarBootstrap["profile"];
export type CalendarGroups = ReturnType<typeof NativeCalendarAccessGroupsSchema.parse>;
export function fetchCalendarGroups() { return calendarRequest("/groups",value=>NativeCalendarAccessGroupsSchema.parse(value)); }
export type CalendarProfiles = ReturnType<typeof NativeCalendarProfilesSchema.parse>;
export function fetchCalendarSubscription(managerId: string) {
  return calendarRequest(`/subscriptions?managerId=${encodeURIComponent(managerId)}`, value => CalendarSubscriptionStateSchema.parse(value));
}
export function mutateCalendarSubscription(body: CalendarSubscriptionMutation, csrfToken: string | null) {
  return calendarRequest("/subscriptions", value => CalendarSubscriptionStateSchema.parse(value), { body, csrfToken });
}

/** Only a server-confirmed existing operator without a native profile may bypass the calendar gate. */
export class CalendarOperatorOnlyError extends ApiRequestError {
  constructor() { super("Календарный профиль не выдан; доступ оператора сохранён.", { status: 403, code: "CALENDAR_ACCESS_NOT_GRANTED" }); }
}

async function calendarRequest<T>(path: string, parse: (value: unknown) => T, mutation?: { body: unknown; csrfToken: string | null }): Promise<T> {
  if (mutation && !mutation.csrfToken) throw new ApiRequestError("Сессия устарела. Войдите снова.", { code: "CSRF_TOKEN_UNAVAILABLE" });
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 20_000);
  try {
    const response = await fetch(`/api/calendar${path}`, {
      method: mutation ? "POST" : "GET", credentials: "include", signal: controller.signal,
      headers: mutation ? { "Content-Type": "application/json", "x-csrf-token": mutation.csrfToken ?? "" } : { Accept: "application/json" },
      ...(mutation ? { body: JSON.stringify(mutation.body) } : {}),
    });
    const payload: unknown = await response.json();
    if (!response.ok) {
      let message = response.status === 409 ? "Расписание изменилось. Обновите его и проверьте выбранное время." : "Не удалось получить ответ календаря.";
      let code: string | undefined;
      if (typeof payload === "object" && payload !== null && "error" in payload && typeof payload.error === "object" && payload.error !== null) {
        if (path === "/bootstrap" && response.status === 403 && "details" in payload.error && typeof payload.error.details === "object" && payload.error.details !== null
          && "calendarCode" in payload.error.details && payload.error.details.calendarCode === "CALENDAR_ACCESS_NOT_GRANTED"
          && "operatorAccess" in payload.error.details && payload.error.details.operatorAccess === true) throw new CalendarOperatorOnlyError();
        if ("message" in payload.error && typeof payload.error.message === "string") message = payload.error.message;
        if ("code" in payload.error && typeof payload.error.code === "string") code = payload.error.code;
      }
      throw new ApiRequestError(message, { status: response.status, ...(code ? { code } : {}) });
    }
    return parse(payload);
  } catch (error: unknown) {
    if (error instanceof ApiRequestError) throw error;
    if (error instanceof DOMException && error.name === "AbortError") throw new ApiRequestError("Сервер не подтвердил действие вовремя. Обновите календарь перед повтором.", { code: "REQUEST_TIMEOUT" });
    throw new ApiRequestError("Не удалось подтвердить данные календаря. Проверьте подключение и повторите запрос.", { code: "CALENDAR_UNAVAILABLE" });
  } finally { window.clearTimeout(timeout); }
}

export function fetchCalendarBootstrap() { return calendarRequest("/bootstrap", (value) => NativeCalendarBootstrapSchema.parse(value)); }
export function fetchCalendar(from: string, to: string) { return calendarRequest(`?${new URLSearchParams({ from, to })}`, (value) => NativeCalendarSnapshotSchema.parse(value)); }
/** The API takes at most 62 days per request (NativeCalendarRangeSchema); a longer period goes as consecutive windows. */
export function webinarEventWindows(from: string, to: string) {
  const day = 86_400_000, end = Date.parse(to), windows: { from: string; to: string }[] = [];
  for (let start = Date.parse(from); start <= end; start += 62 * day) {
    windows.push({ from: new Date(start).toISOString().slice(0, 10), to: new Date(Math.min(start + 61 * day, end)).toISOString().slice(0, 10) });
  }
  return windows;
}
export async function fetchWebinarEvents(from: string, to: string) {
  const parts = await Promise.all(webinarEventWindows(from, to).map(range => calendarRequest(`/events?${new URLSearchParams(range)}`, value => {
    if (typeof value !== "object" || value === null || !("events" in value)) throw new Error("Missing webinar events.");
    return NativeCalendarEventSchema.array().parse(value.events);
  })));
  return { events: [...new Map(parts.flat().map(event => [event.id, event])).values()] };
}
export function resolveCalendarLead(leadUrl: string, csrfToken: string | null) { return calendarRequest("/lead/resolve", (value) => NativeCalendarLeadResultSchema.parse(value), { body: { leadUrl }, csrfToken }); }
export function mutateCalendar(path: string, body: unknown, csrfToken: string | null) { return calendarRequest(path, (value) => NativeCalendarMutationResultSchema.parse(value), { body, csrfToken }); }
export function mutateCalendarBooking(path: string, body: unknown, csrfToken: string | null) { return calendarRequest(path, (value) => NativeCalendarBookingResultSchema.parse(value), { body, csrfToken }); }
export type CalendarSeriesList = ReturnType<typeof NativeCalendarSeriesListSchema.parse>;
/** Recent broadcast series (days and follow-up) for the event form. */
export function fetchCalendarSeries() { return calendarRequest("/series", (value) => NativeCalendarSeriesListSchema.parse(value)); }
export function fetchCalendarProfiles() { return calendarRequest("/profiles", (value) => NativeCalendarProfilesSchema.parse(value)); }
export type CalendarBookingChannels = ReturnType<typeof NativeCalendarBookingChannelsSchema.parse>;
/** «Куда пишем»: the booking's person channels with health and the chosen one. */
export function fetchCalendarBookingChannels(bookingId: string) {
  return calendarRequest(`/bookings/${encodeURIComponent(bookingId)}/channels`, (value) => NativeCalendarBookingChannelsSchema.parse(value));
}
/** A staff choice for this booking version; null returns to the shared rule. */
export function chooseCalendarBookingChannel(bookingId: string, expectedVersion: number, channelKey: string | null, csrfToken: string | null) {
  return calendarRequest(`/bookings/${encodeURIComponent(bookingId)}/channel`, (value) => NativeCalendarBookingChannelsSchema.parse(value),
    { body: { expectedVersion, channelKey }, csrfToken });
}

export function canEditCalendarHours(profile: CalendarProfile, managerId: string): boolean {
  return profile.role === "leader" || (profile.role === "manager" && profile.managerId === managerId);
}
export function canBookCalendarSlot(profile: CalendarProfile, managerId: string): boolean {
  return profile.role === "leader" || profile.role === "caller" || profile.managerId === managerId;
}
export function canChangeCalendarBooking(profile: CalendarProfile, actorId: string | null, booking: CalendarBooking): boolean {
  return profile.role === "leader" || (profile.role === "manager" && profile.managerId === booking.managerId) || (profile.role === "caller" && actorId !== null && (booking.createdBy === actorId || (booking.createdBy === null && booking.origin === "yulia")));
}
